import React, { memo, useState, Suspense, useEffect, useMemo } from "react";
import {
	Grid,
	AppBar,
	Tabs,
	Tab,
	Theme,
	Toolbar,
	Select,
	FormControl,
	MenuItem,
	Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { useResource, useFetcher } from "rest-hooks";
import DashboardBanner from "../../../images/dashboard_banner.jpeg";
import {
	InvoicesResource,
	BackofficeApplicationResource,
	PaymentNoticesResource,
	LoginResource,
} from "../../../resources";
import OverviewTabPage from "./OverviewTabPage";
import InvoicesTabPage from "./InvoicesTabPage";
import PaymentPlanTabPage from "./PaymentPlanTabPage";
import { useCommonWidths } from "../../../hooks";
import { RouteComponentProps } from "@reach/router";
import { MyPageModal, ProgressIndicator, MyPagePanel } from "../../common";
import { defineMessages, useIntl } from "react-intl";
import { InvoiceStatusEnum, InvoiceTypeEnum } from "../../../utils";
import DocumentsTabPage from "./DocumentsTabPage";

interface DashboardProps extends RouteComponentProps {
	id?: number;
}

type ErrorMessageTypes =
	| "noBackofficeApplication"
	| "toDebtCollection"
	| "toDebtCollection2"
	| "leasingContract"
	| "";

const useStyles = makeStyles((theme: Theme) => ({
	appBar: {
		backgroundColor: "#fff",
		color: "#000",
		borderBottom: `1px solid ${theme.palette.primary.main}`,
		boxShadow: "none",
	},
	appTab: {
		padding: "12px 16px",
		fontSize: 20,
		fontWeight: "bold",
		[theme.breakpoints.down("sm")]: {
			fontSize: 14,
		},
	},
	imgWrapper: {
		maxHeight: 300,
		[theme.breakpoints.down("sm")]: {
			maxHeight: 120,
		},
	},
	img: {
		backgroundImage: `url(${DashboardBanner})`,
		backgroundSize: "cover",
		backgroundRepeat: "no-repeat",
		backgroundPosition: "center",
		objectFit: "cover",
		width: "100%",
		height: 300,
		maxHeight: "inherit",
	},
	tabPanelContentWrapper: {
		width: "inherit",
	},
	formControl: {
		minWidth: 120,
	},
	dropDownAdministrate: {
		color: "#05A",
		fontWeight: "bold",
	},
}));

const changeToTabPanel = (index: number) => {
	return {
		id: `backofficeApplication-tab-${index}`,
		"aria-controls": `backofficeApplication-tabpanel-${index}`,
	};
};

const invoiceBillTypes = [
	InvoiceTypeEnum.TermBill,
	InvoiceTypeEnum.FulfillmentBill,
];

interface AppTabs {
	name: string;
	active: boolean;
	hideCondition?: (value: BackofficeApplicationResource) => boolean;
	children: (props: AppTabsChildrenProps) => React.ReactNode;
}

interface AppTabsChildrenProps {
	backofficeApplication?: BackofficeApplicationResource;
	invoices?: InvoicesResource[];
	paymentNotices?: PaymentNoticesResource[];
	setTabValue?: (index: number) => void;
	modalContent?: string;
	handleDropdownSelectChange?: (event: any) => void;
}

const appbarTabs: AppTabs[] = [
	{
		name: "Oversikt",
		active: true,
		children: (props: AppTabsChildrenProps) => (
			<OverviewTabPage
				backofficeApplication={props.backofficeApplication!}
				invoices={props.invoices!.filter((i) =>
					invoiceBillTypes.includes(i.invoiceType)
				)}
				paymentNotices={props.paymentNotices!}
				// accountTransactions={accountTransactions}
				onShowMore={(index: number) => props.setTabValue!(index)}
				isFulfilled={props.backofficeApplication!.fulfilled}
				actionControl={
					<DropdownActionMenu
						modalContent={props.modalContent!}
						handleDropdownSelectChange={props.handleDropdownSelectChange!}
					/>
				}
			/>
		),
	},
	{
		name: "Fakturaer",
		active: true,
		children: (props: AppTabsChildrenProps) => (
			<InvoicesTabPage
				backofficeApplication={props.backofficeApplication!}
				invoices={props.invoices!.filter((i) =>
					invoiceBillTypes.includes(i.invoiceType)
				)}
				paymentNotices={props.paymentNotices!}
			/>
		),
	},
	{
		name: "Betalingsplan",
		active: true,
		hideCondition: (b) => {
			return b.fulfilled;
		},
		children: (props: AppTabsChildrenProps) => (
			<PaymentPlanTabPage
				backofficeApplication={props.backofficeApplication!}
				interestRateChanges={props.invoices?.filter(
					(x) =>
						x.invoiceType === InvoiceTypeEnum.ChangeInterest &&
						x.status === InvoiceStatusEnum.NotBilled
				)}
			/>
		),
	},
	{
		name: "Dokumenter",
		active: true,
		children: (props: AppTabsChildrenProps) => (
			<DocumentsTabPage
				backofficeApplicationId={props.backofficeApplication?.id!.toString()}
			/>
		),
	},
	{
		name: "Siste bevegelser",
		active: false,
		children: (props: AppTabsChildrenProps) => <></>,
	},
];

const Dashboard = (props: DashboardProps) => {
	return (
		<Suspense
			fallback={
				<ProgressIndicator
					type="circular"
					wrapperHeight="calc(100vh - 435px)"
				/>
			}
		>
			<DashboardInternal {...props} />
		</Suspense>
	);
};

const DashboardInternal = memo((props: DashboardProps) => {
	const backofficeApplicationIdToFetch = props.id;
	const { isSmDown: isMobile } = useCommonWidths();

	const [modalContent, setModalContent] = useState("");
	const handleDropdownSelectChange = (event: any) => {
		setModalContent(event.target.value);
	};

	const backofficeApplication = useResource(
		BackofficeApplicationResource.detailShape(),
		{
			id: backofficeApplicationIdToFetch,
		}
	);

	const dashboardTabsToRender = appbarTabs.filter((tab: AppTabs) => {
		//	Render if (active and hideCondition returns false) or (active and not having hideCondition)
		return (
			tab.active &&
			(!tab.hideCondition || tab.hideCondition(backofficeApplication) === false)
		);
	});

	const [tabValue, setTabValue] = useState(0);
	const handleTabPageChange = (event: any, newValue: any) => {
		setTabValue(newValue);
	};

	const contractErrorReason: ErrorMessageTypes =
		backofficeApplication.toDebtCollection
			? (backofficeApplication.debtCollectionCode === "A" ? "toDebtCollection2" : "toDebtCollection")
			: backofficeApplication.id === 0
			? "noBackofficeApplication"
			: backofficeApplication.isLeasingContract
			? "leasingContract"
			: "";

	const loginStatus = useFetcher(LoginResource.statusShape());
	useEffect(() => {
		async function fetchLoginStatus() {
			const { isAuthenticated } = await loginStatus({});
		}
		fetchLoginStatus();
	}, [loginStatus]);

	useEffect(() => {
		setTabValue(tabValue >= dashboardTabsToRender.length ? 0 : tabValue);
	}, [dashboardTabsToRender.length, tabValue]);

	return (
		<Grid container direction="column">
			<DashboardHeader
				tabs={dashboardTabsToRender}
				isMobile={isMobile}
				modalContent={modalContent}
				handleDropdownSelectChange={handleDropdownSelectChange}
				handleTabPageChange={handleTabPageChange}
				tabValue={tabValue}
			/>
			<Suspense
				fallback={
					<ProgressIndicator
						type="circular"
						wrapperHeight="calc(100vh - 435px)"
					/>
				}
			>
				<TabPageViews
					contractErrorReason={contractErrorReason}
					backofficeApplication={backofficeApplication}
					modalContent={modalContent}
					tabValue={tabValue}
					handleDropdownSelectChange={handleDropdownSelectChange}
					setTabValue={setTabValue}
					setModalContent={setModalContent}
					tabs={dashboardTabsToRender}
				/>
			</Suspense>
		</Grid>
	);
});

//	User by both <DashboardHeader> and <TabPageViews>
interface DashboardCommonProps {
	modalContent: string;
	tabValue: number;
	handleDropdownSelectChange: (e: any) => void;
}

interface DashboardHeaderProps extends DashboardCommonProps {
	isMobile: boolean;
	handleTabPageChange: (e: any, newVal: any) => void;
	tabs: AppTabs[];
}

const DashboardHeader = (props: DashboardHeaderProps) => {
	const { isMobile, handleTabPageChange, tabValue, tabs } = props;

	const classes = useStyles();

	return (
		<>
			<Grid item className={classes.imgWrapper} xs={12}>
				<div className={classes.img}></div>
			</Grid>
			<AppBar position="static" className={classes.appBar}>
				<Toolbar>
					<Tabs
						value={tabValue}
						onChange={handleTabPageChange}
						aria-label="BackofficeApplicationDashboard navigation menu"
						indicatorColor="primary"
						variant={isMobile ? "scrollable" : "standard"}
						style={{ flexGrow: 1 }}
					>
						{tabs.map((tab: AppTabs, idx) => {
							return (
								<Tab
									key={idx}
									label={tab.name}
									{...changeToTabPanel(idx)}
									value={idx}
									className={classes.appTab}
								/>
							);
						})}
					</Tabs>
				</Toolbar>
			</AppBar>
		</>
	);
};

interface TabPageViewsProps extends DashboardCommonProps {
	backofficeApplication: BackofficeApplicationResource;
	setTabValue: (val: number) => void;
	setModalContent: (val: string) => void;
	contractErrorReason?: ErrorMessageTypes;
	tabs: AppTabs[];
}

const TabPageViews = (props: TabPageViewsProps) => {
	const {
		contractErrorReason,
		backofficeApplication,
		modalContent,
		tabValue,
		handleDropdownSelectChange,
		setTabValue,
		setModalContent,
		tabs,
	} = props;

	const invoices = useResource(InvoicesResource.listShape(), {
		id: backofficeApplication.id,
	});
	const paymentNotices = useResource(PaymentNoticesResource.listShape(), {
		id: backofficeApplication.id,
	});

	if (contractErrorReason)
		return (
			<ErrorWithBackofficeApplication
				reason={contractErrorReason}
				idToFetch={backofficeApplication.id}
			/>
		);

	return (
		<>
			{tabs.map((tab, idx) => {
				return (
					<TabPanel tabValue={tabValue} index={idx} key={idx}>
						{tab.children({
							backofficeApplication: backofficeApplication,
							paymentNotices: paymentNotices,
							invoices: invoices,
							modalContent: modalContent,
							setTabValue: setTabValue,
							handleDropdownSelectChange: handleDropdownSelectChange,
						} as AppTabsChildrenProps)}
					</TabPanel>
				);
			})}

			<MyPageModal
				modalContent={modalContent}
				setModalContent={(val: string) => setModalContent(val)}
				backofficeApplication={backofficeApplication}
			/>
		</>
	);
};

interface TabPanelProps {
	tabValue?: any;
	index?: number | undefined;
	children?: React.ReactNode | boolean;
}

const TabPanel = (props: TabPanelProps) => {
	const { tabValue, index, children } = props;
	const classes = useStyles();

	return tabValue === index ? (
		<div
			className={classes.tabPanelContentWrapper}
			role="tabpanel"
			id={`backofficeApplication-tabpanel-${index}`}
			aria-labelledby={`backofficeApplication-tab-${index}`}
		>
			{children}
		</div>
	) : null;
};

interface DropdownActionMenuProps {
	modalContent: string;
	handleDropdownSelectChange: (evt: any, val: any) => void;
}

const DropdownActionMenu = (props: DropdownActionMenuProps) => {
	const classes = useStyles();
	const { modalContent, handleDropdownSelectChange } = props;

	return (
		<FormControl className={classes.formControl}>
			<Select
				value={modalContent}
				onChange={handleDropdownSelectChange}
				className={classes.dropDownAdministrate}
				displayEmpty
			>
				<MenuItem value="">
					<em>Administrer avtalen ...</em>
				</MenuItem>
				<MenuItem value={"FulfillContract"}>Innfrielse av kontrakt</MenuItem>
				<MenuItem value={"biggerPayment"}>Betal inn ekstra</MenuItem>
				{/* <MenuItem tabValue={"extendLoan"}>Utsett betaling</MenuItem>
				<MenuItem tabValue={"cancelLoan"}>Avslutt avtalen</MenuItem> */}
			</Select>
		</FormControl>
	);
};

interface ErrorProps {
	reason: ErrorMessageTypes;
	idToFetch?: number;
}

const ErrorWithBackofficeApplication = (props: ErrorProps) => {
	const isMobile = useCommonWidths().isSmDown;
	const { reason, idToFetch } = props;

	const errorMessages = defineMessages({
		noBackofficeApplication: {
			id: "minside.dashboard.errorMessage.noBackofficeApplication",
			defaultMessage: "Fant ikke kontrakt med nummer {id}",
			description: "No backofficeApplication",
			values: {
				id: idToFetch,
			},
		},
		toDebtCollection: {
			id: "minside.dashboard.errorMessage.toDebtCollection",
			defaultMessage:
				"Denne kontrakten er til inkasso. Ta kontakt med {provider} telefon {phone} eller {mail}",
			description: "BackofficeApplication is to debt collection",
			values: {
				provider: "Intrum",
				mail: (
					<a href="mailto:kundesenter@intrum.com">kundesenter@intrum.com</a>
				),
				phone: "73 54 20 30",
			},
		},
		toDebtCollection2: {
			id: "minside.dashboard.errorMessage.toDebtCollection2",
			defaultMessage:
				"Denne kontrakten er til inkasso. Ta kontakt med {provider} telefon {phone} eller {mail}",
			description: "BackofficeApplication is to debt collection",
			values: {
				provider: "Axactor",
				mail: (
					<a href="mailto:objekt@axactor.no">objekt@axactor.no</a>
				),
				phone: "21 65 11 60",
			},
		},
		leasingContract: {
			id: "minside.dashboard.errorMessage.leasingContract",
			defaultMessage:
				"Leasing-kontrakter vises ikke på Min Side - har du spørsmål ifb med leasing ta kontakt med oss på {mail}.",
			description: "BackofficeApplication is a leasing contract",
			values: {
				mail: <a href="mailto:post@financiering.no">post@financiering.no</a>,
			},
		},
		"": {
			id: "",
			defaultMessage: "",
			description: "",
			values: {},
		},
	});

	const message = errorMessages[reason];

	return (
		<MyPagePanel>
			<Typography
				variant={isMobile ? "h6" : "h4"}
				style={{ textAlign: "center" }}
			>
				{useIntl().formatMessage(message, message.values)}
			</Typography>
		</MyPagePanel>
	);
};

export default Dashboard;
